import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { customerMutations, customerQueryKeys } from '@as-react/common-queries';
import { CountryId, ErrorMessage } from '@as-react/common-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Checkbox, Divider, Grid, Stack, useMediaQueryMatchesOnMobile } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { DELAY } from '../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { getAddressValidationSchema, prepareAddressByCountry } from '../../form/address-fields/addressFields.utils';
import { FormField } from '../../form/FormField';
import { useScrollToFirstFormError } from '../../form/hooks/useScrollToFirstFormError';
import { AddressFieldsWithCountrySelector } from '../../form/profile-fields/AddressFieldsWithCountrySelector';
import { CustomerDetailsFields } from '../../form/profile-fields/CustomerDetailsFields';
import { TitleField } from '../../form/profile-fields/TitleField';
const FORM_NAME = 'customer-address-edit';
export const emptyAddress = {
    addToProfile: true,
    country: '',
    countryCode: '',
    idCountry: 0,
    idTitle: undefined,
    firstName: '',
    lastName: '',
    middleName: '',
    companyName: '',
    customTown: false,
    address: '',
    houseNumber: '',
    houseNumberAddition: '',
    postCode: '',
    city: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    county: '',
    defaultAddress: false,
    deliveryName: '',
    deliveryAddressId: 0,
    isActive: null,
    mainAddress: false,
    state: '',
    stateIso: '',
    phoneNumber: '',
    finder: '',
};
export const CustomerAddressEdit = ({ initAddress, handleCancelClick, onCancelEmptyForm, isAddAddress, }) => {
    const { t } = useTranslation([I18N_NAMESPACES.account, I18N_NAMESPACES.general]);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const [activeCountry, setActiveCountry] = useState();
    const showMiddleName = defaultRequestParams.market === 'nl' || (activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.idCountry) === CountryId.NL;
    const isMobile = useMediaQueryMatchesOnMobile();
    const queryClient = useQueryClient();
    const { updateDeliveryAddressMutation, addDeliveryAddressMutation } = customerMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutate: addOneDeliveryAddress, isSuccess: isSuccessAdd } = useMutation(addDeliveryAddressMutation());
    const { mutate: updateOneDeliveryAddress, isSuccess: isSuccessUpdate } = useMutation(updateDeliveryAddressMutation());
    const addressValidationSchema = Yup.object({
        firstName: Yup.string().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        lastName: Yup.string().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
        idTitle: Yup.number().required(t(ErrorMessage.ACCOUNT_REQUIRED)),
    }).concat(getAddressValidationSchema({
        country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode,
        t,
        prefix: 'address.',
        isOptional: false,
    }));
    const { idCountry, customTown, address, houseNumber, houseNumberAddition, postCode, city, addressLine2, addressLine3, addressLine4, county, state, stateIso } = initAddress, rest = __rest(initAddress, ["idCountry", "customTown", "address", "houseNumber", "houseNumberAddition", "postCode", "city", "addressLine2", "addressLine3", "addressLine4", "county", "state", "stateIso"]);
    const formProps = useForm({
        defaultValues: Object.assign(Object.assign({}, rest), { address: {
                idCountry,
                customTown,
                address,
                houseNumber,
                houseNumberAddition,
                postCode,
                city,
                addressLine2,
                addressLine3,
                addressLine4,
                county,
                state,
                stateIso,
            } }),
        mode: 'onTouched',
        resolver: yupResolver(addressValidationSchema),
    });
    useScrollToFirstFormError(formProps.formState, true);
    useEffect(() => {
        if (activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode) {
            formProps.setValue('address.country', activeCountry.countryCode);
        }
    }, [activeCountry, formProps]);
    const handleSubmit = (_a) => __awaiter(void 0, void 0, void 0, function* () {
        var { address } = _a, rest = __rest(_a, ["address"]);
        const values = Object.assign(Object.assign({}, rest), address);
        const preparedAddress = prepareAddressByCountry(values, '');
        const updateMutation = isAddAddress ? addOneDeliveryAddress : updateOneDeliveryAddress;
        updateMutation(preparedAddress, {
            onSuccess: () => {
                if (onCancelEmptyForm)
                    onCancelEmptyForm();
                setTimeout(() => {
                    handleCancelClick(initAddress.deliveryAddressId);
                    queryClient.invalidateQueries({ queryKey: customerQueryKeys.getDeliveryAddresses() });
                }, DELAY.DELAY_3000);
            },
            onError: () => {
                ScrollUtil.scrollToErrorElement(document.querySelector(`[name="${FORM_NAME}"]`));
            },
        });
    });
    const handleCancelAction = () => {
        onCancelEmptyForm ? onCancelEmptyForm() : handleCancelClick(initAddress.deliveryAddressId);
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), noValidate: true, name: FORM_NAME, children: _jsxs(Stack, { direction: "col", gap: "4", children: [_jsx(TitleField, { fieldName: "idTitle" }), _jsx(CustomerDetailsFields, { showMiddleName: showMiddleName }), _jsx(Divider, {}), _jsx(AddressFieldsWithCountrySelector, { isRequired: true, country: activeCountry === null || activeCountry === void 0 ? void 0 : activeCountry.countryCode, showCompanyName: true, onCountryChange: setActiveCountry, hasInitialAddress: isAddAddress, showCard: false }), _jsx(FormField, { name: "defaultAddress", children: (_a) => {
                            var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]);
                            return (_jsx(Checkbox, Object.assign({ id: "defaultAddress", label: t('delivery.default-delivery-address') }, fieldProps, { checked: fieldProps.value })));
                        } }), _jsxs(Grid, { columns: 2, gap: "4", children: [_jsx(Button, { fullWidth: true, type: "submit", testId: "save_button", state: formProps.formState.isSubmitting ? 'loading' : 'interactive', children: t(`profileDetails.form.save.action${isMobile ? '.mobile' : ''}`) }), _jsx("div", { children: _jsx(Button, { fullWidth: isMobile, testId: "cancel_button", variant: "secondary", onClick: handleCancelAction, children: t('profileDetails.form.cancel.action') }) })] }), isSuccessAdd || isSuccessUpdate ? (_jsx(Banner, { text: t('profileDetails.form.save.finished'), status: "success" })) : null] }) }) })));
};
